import { useState, useEffect, useRef, useCallback, type RefObject } from "react";
import { api } from "../lib/api";
import { X } from "lucide-react";
import styles from "./ChatView.module.css";

export interface SearchResult {
  id: string;
  channelId: string;
  senderId: string;
  senderName?: string;
  ciphertext: string;
  contentType: string;
  createdAt: string;
  plaintext?: string;
}

export interface SearchBarProps {
  channelId: string;
  searchInputRef: RefObject<HTMLInputElement | null>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  matchCount: number;
  onClose: () => void;
  onResultClick: (messageId: string) => void;
}

export function SearchBar({
  channelId,
  searchInputRef,
  searchQuery,
  onSearchChange,
  matchCount,
  onClose,
  onResultClick,
}: SearchBarProps) {
  const [apiResults, setApiResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced API search
  const doSearch = useCallback(
    (q: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!q.trim()) {
        setApiResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      debounceRef.current = setTimeout(async () => {
        try {
          const data = await api.get<{ results: SearchResult[]; total: number }>(
            `/channels/${channelId}/messages/search?q=${encodeURIComponent(q.trim())}&limit=20`,
          );
          setApiResults(data.results);
        } catch {
          setApiResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    },
    [channelId],
  );

  useEffect(() => {
    doSearch(searchQuery);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, doSearch]);

  // Handle ESC key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className={styles.searchBar}>
      <input
        ref={searchInputRef}
        type="text"
        placeholder="Nachrichten durchsuchen..."
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className={styles.searchInput}
      />
      {searchQuery && (
        <span className={styles.searchCount}>
          {isSearching ? "..." : `${matchCount + apiResults.length} Treffer`}
        </span>
      )}
      <button className={styles.searchCloseBtn} onClick={onClose} aria-label="Suche schließen">
        <X size={16} />
      </button>

      {/* API search results dropdown */}
      {searchQuery && apiResults.length > 0 && (
        <div className={styles.searchResults}>
          {apiResults.map((r) => {
            const text = r.ciphertext ?? "";
            const time = new Date(r.createdAt).toLocaleString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            });
            return (
              <button
                key={r.id}
                className={styles.searchResultItem}
                onClick={() => onResultClick(r.id)}
              >
                <span className={styles.searchResultSender}>
                  {r.senderName ?? "Unbekannt"}
                </span>
                <span className={styles.searchResultText}>
                  {text.length > 80 ? text.slice(0, 80) + "…" : text}
                </span>
                <span className={styles.searchResultTime}>{time}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
